import IntranetLayout from "@/components/IntranetLayout";
import { useState } from "react";
import { FileText, FolderOpen, ChevronRight, ChevronDown, Search, Filter, Eye, Upload, Send, Bell, Clock, CheckCircle, AlertTriangle, RotateCcw, Download, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";

/* ── Category Tree ── */
const CATEGORIES = [
  { name: "Safety Management System", children: ["SMS Manual", "Company Policies", "Emergency Response Plans", "Risk Assessments"] },
  { name: "Operations", children: ["Standing Orders", "Passage Planning", "Cargo / Passenger Operations", "DP Operations", "Fuel Operations"] },
  { name: "Maintenance", children: ["Planned Maintenance System (PMS)", "Critical Equipment Procedures", "Repair Procedures"] },
  { name: "Crew & HR", children: ["Crew Handbook", "Leave & Rotation Policy", "D&A Policy", "Onboarding Packs"] },
  { name: "Commercial", children: ["Charter Party Templates", "Proposal Templates", "Rate Cards (restricted)"] },
  { name: "Compliance", children: ["OVMSA2 Evidence Documents", "Audit Reports", "Non-Conformance Reports"] },
  { name: "Corporate", children: ["LLCA & Amendments", "NDAs (active)", "Insurance Policies"] },
];

type DocStatus = "active" | "under_review" | "draft" | "superseded" | "overdue";

interface ControlledDoc {
  id: string;
  ref: string;
  title: string;
  rev: string;
  category: string;
  status: DocStatus;
  vessels: string[];
  reviewDue: string;
  ackRate: [number, number];
  author: string;
  approver: string;
  issueDate: string;
  revHistory: { rev: string; date: string; change: string; approvedBy: string }[];
}

const DOCS: ControlledDoc[] = [
  { id: "1", ref: "SMS-001", title: "VOFF Safety Management System Manual", rev: "Rev 1", category: "SMS Manual", status: "active", vessels: ["All"], reviewDue: "2026-09-01", ackRate: [18, 20], author: "K. Pemberton", approver: "J. Arnold", issueDate: "2026-01-15", revHistory: [{ rev: "Rev 0", date: "2025-06-01", change: "Initial issue", approvedBy: "J. Arnold" }, { rev: "Rev 1", date: "2026-01-15", change: "Updated emergency contacts, OVMSA2 alignment", approvedBy: "J. Arnold" }] },
  { id: "2", ref: "OPS-001", title: "Bridge Standing Orders", rev: "Rev 0", category: "Standing Orders", status: "active", vessels: ["All"], reviewDue: "2026-07-15", ackRate: [20, 20], author: "E. Ercegovic", approver: "K. Pemberton", issueDate: "2025-11-01", revHistory: [{ rev: "Rev 0", date: "2025-11-01", change: "Initial issue", approvedBy: "K. Pemberton" }] },
  { id: "3", ref: "OPS-002", title: "DP Operations Manual", rev: "Rev 0", category: "DP Operations", status: "active", vessels: ["All"], reviewDue: "2026-08-01", ackRate: [16, 20], author: "E. Ercegovic", approver: "K. Pemberton", issueDate: "2025-11-15", revHistory: [{ rev: "Rev 0", date: "2025-11-15", change: "Initial issue", approvedBy: "K. Pemberton" }] },
  { id: "4", ref: "OPS-003", title: "Passage Planning Procedure", rev: "Rev 0", category: "Passage Planning", status: "overdue", vessels: ["All"], reviewDue: "2026-03-15", ackRate: [12, 20], author: "E. Ercegovic", approver: "K. Pemberton", issueDate: "2025-09-15", revHistory: [{ rev: "Rev 0", date: "2025-09-15", change: "Initial issue", approvedBy: "K. Pemberton" }] },
  { id: "5", ref: "OPS-004", title: "Man Overboard Procedure", rev: "Rev 0", category: "Emergency Response Plans", status: "active", vessels: ["All"], reviewDue: "2026-09-01", ackRate: [20, 20], author: "E. Ercegovic", approver: "J. Arnold", issueDate: "2025-10-01", revHistory: [{ rev: "Rev 0", date: "2025-10-01", change: "Initial issue", approvedBy: "J. Arnold" }] },
  { id: "6", ref: "OPS-005", title: "Fire & Abandon Ship Procedure", rev: "Rev 0", category: "Emergency Response Plans", status: "active", vessels: ["All"], reviewDue: "2026-10-01", ackRate: [17, 20], author: "E. Ercegovic", approver: "J. Arnold", issueDate: "2025-10-01", revHistory: [{ rev: "Rev 0", date: "2025-10-01", change: "Initial issue", approvedBy: "J. Arnold" }] },
  { id: "7", ref: "OPS-006", title: "Crew Transfer Operations SOP", rev: "Rev 0", category: "Cargo / Passenger Operations", status: "active", vessels: ["All"], reviewDue: "2026-11-01", ackRate: [19, 20], author: "E. Ercegovic", approver: "K. Pemberton", issueDate: "2025-11-01", revHistory: [{ rev: "Rev 0", date: "2025-11-01", change: "Initial issue", approvedBy: "K. Pemberton" }] },
  { id: "8", ref: "OPS-007", title: "Fuel Bunkering Procedure", rev: "Rev 0", category: "Fuel Operations", status: "under_review", vessels: ["All"], reviewDue: "2026-06-01", ackRate: [14, 20], author: "E. Ercegovic", approver: "K. Pemberton", issueDate: "2025-12-01", revHistory: [{ rev: "Rev 0", date: "2025-12-01", change: "Initial issue", approvedBy: "K. Pemberton" }] },
  { id: "9", ref: "MNT-001", title: "Planned Maintenance System Guide", rev: "Rev 0", category: "Planned Maintenance System (PMS)", status: "active", vessels: ["All"], reviewDue: "2026-12-01", ackRate: [20, 20], author: "E. Ercegovic", approver: "K. Pemberton", issueDate: "2026-01-01", revHistory: [{ rev: "Rev 0", date: "2026-01-01", change: "Initial issue", approvedBy: "K. Pemberton" }] },
  { id: "10", ref: "MNT-002", title: "Critical Equipment Override Procedure", rev: "Rev 0", category: "Critical Equipment Procedures", status: "active", vessels: ["All"], reviewDue: "2026-12-01", ackRate: [18, 20], author: "E. Ercegovic", approver: "K. Pemberton", issueDate: "2026-01-15", revHistory: [{ rev: "Rev 0", date: "2026-01-15", change: "Initial issue", approvedBy: "K. Pemberton" }] },
  { id: "11", ref: "CRW-001", title: "Crew Handbook", rev: "Rev 0", category: "Crew Handbook", status: "under_review", vessels: ["All"], reviewDue: "2026-05-01", ackRate: [10, 20], author: "K. Pemberton", approver: "J. Arnold", issueDate: "2025-08-01", revHistory: [{ rev: "Rev 0", date: "2025-08-01", change: "Initial issue", approvedBy: "J. Arnold" }] },
  { id: "12", ref: "CRW-002", title: "D&A Policy & Testing Procedure", rev: "Rev 0", category: "D&A Policy", status: "active", vessels: ["All"], reviewDue: "2026-08-01", ackRate: [20, 20], author: "K. Pemberton", approver: "J. Arnold", issueDate: "2025-09-01", revHistory: [{ rev: "Rev 0", date: "2025-09-01", change: "Initial issue", approvedBy: "J. Arnold" }] },
  { id: "13", ref: "CRW-003", title: "Hours of Rest Policy (MLC)", rev: "Rev 0", category: "Leave & Rotation Policy", status: "active", vessels: ["All"], reviewDue: "2026-08-01", ackRate: [19, 20], author: "K. Pemberton", approver: "J. Arnold", issueDate: "2025-09-01", revHistory: [{ rev: "Rev 0", date: "2025-09-01", change: "Initial issue", approvedBy: "J. Arnold" }] },
  { id: "14", ref: "COMP-001", title: "OVMSA2 Self-Assessment Record", rev: "Rev 0", category: "OVMSA2 Evidence Documents", status: "active", vessels: ["All"], reviewDue: "2026-10-01", ackRate: [15, 20], author: "K. Pemberton", approver: "J. Arnold", issueDate: "2026-02-01", revHistory: [{ rev: "Rev 0", date: "2026-02-01", change: "Initial issue", approvedBy: "J. Arnold" }] },
  { id: "15", ref: "Corp008", title: "Maritime Cyber Security Policy", rev: "Rev 0", category: "Company Policies", status: "active", vessels: ["All"], reviewDue: "2026-12-01", ackRate: [16, 20], author: "K. Pemberton", approver: "J. Arnold", issueDate: "2026-01-01", revHistory: [{ rev: "Rev 0", date: "2026-01-01", change: "Initial issue", approvedBy: "J. Arnold" }] },
  { id: "16", ref: "SEC-001", title: "Anti-Piracy Procedure", rev: "Rev 0", category: "Standing Orders", status: "draft", vessels: ["Nigeria Fleet"], reviewDue: "2026-06-01", ackRate: [0, 8], author: "E. Ercegovic", approver: "K. Pemberton", issueDate: "-", revHistory: [] },
];

const ACTIVITY_FEED = [
  { text: "OPS-002 Rev 0 acknowledged by A. Santos", time: "2 hours ago" },
  { text: "New revision initiated: CRW-001 — by K. Pemberton", time: "1 day ago" },
  { text: "Reminder sent to J. Okoro (OPS-005)", time: "2 days ago" },
  { text: "SMS-001 Rev 1 acknowledged by M. Fernandez", time: "3 days ago" },
  { text: "COMP-001 distributed to 20 crew", time: "5 days ago" },
  { text: "OPS-006 acknowledged by C. Williams", time: "1 week ago" },
];

const statusBadge = (s: DocStatus) => {
  const m: Record<DocStatus, { label: string; cls: string }> = {
    active: { label: "ACTIVE", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
    under_review: { label: "UNDER REVIEW", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
    draft: { label: "DRAFT", cls: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
    superseded: { label: "SUPERSEDED", cls: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30" },
    overdue: { label: "REVIEW OVERDUE", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
  };
  return <Badge variant="outline" className={`text-[10px] font-bold ${m[s].cls}`}>{m[s].label}</Badge>;
};

export const DocumentControllerContent = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<string[]>(CATEGORIES.map(c => c.name));
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedDoc, setSelectedDoc] = useState<ControlledDoc | null>(null);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionDoc, setRevisionDoc] = useState<ControlledDoc | null>(null);

  const toggleSection = (name: string) => setExpandedSections(prev => prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]);

  const filteredDocs = DOCS.filter(d => {
    if (selectedCategory && d.category !== selectedCategory) return false;
    if (statusFilter !== "all" && d.status !== statusFilter) return false;
    if (search && !d.title.toLowerCase().includes(search.toLowerCase()) && !d.ref.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const pendingAck = DOCS.filter(d => d.ackRate[0] < d.ackRate[1]).length;
  const reviewDue = DOCS.filter(d => d.status === "overdue" || d.status === "under_review").length;
  const unacknowledged = DOCS.reduce((acc, d) => acc + (d.ackRate[1] - d.ackRate[0]), 0);

  return (
    <>
      {/* Header */}
      <div className="bg-[#00283C] rounded-xl p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-[#288CC8] text-xl font-bold tracking-wider uppercase font-display">Document Controller</h1>
            <p className="text-white/60 text-sm font-body mt-1">Controlled Document Management — ISM Compliant</p>
          </div>
          <div className="flex flex-wrap gap-3">
            {[{ label: "Total Documents", value: DOCS.length }, { label: "Pending Ack", value: pendingAck }, { label: "Review Due", value: reviewDue }, { label: "Unacknowledged", value: unacknowledged }].map(s => (
              <div key={s.label} className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-center min-w-[100px]">
                <p className="text-white text-lg font-bold font-display">{s.value}</p>
                <p className="text-white/50 text-[10px] uppercase tracking-wider font-body">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Left: Category Tree */}
        <div className="w-full lg:w-[260px] shrink-0">
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 font-body">Categories</h3>
            <button onClick={() => setSelectedCategory(null)} className={`w-full text-left px-3 py-2 rounded-lg text-sm font-body mb-1 ${!selectedCategory ? "bg-[#288CC8]/10 text-[#288CC8] font-medium" : "text-muted-foreground hover:bg-muted"}`}>
              All Documents ({DOCS.length})
            </button>
            {CATEGORIES.map(cat => {
              const isExpanded = expandedSections.includes(cat.name);
              return (
                <div key={cat.name} className="mb-1">
                  <button onClick={() => toggleSection(cat.name)} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm font-body text-foreground hover:bg-muted rounded-lg">
                    {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                    <FolderOpen className="w-3.5 h-3.5 text-[#288CC8]" />
                    <span className="truncate">{cat.name}</span>
                  </button>
                  {isExpanded && cat.children.map(child => {
                    const count = DOCS.filter(d => d.category === child).length;
                    const hasWarning = DOCS.some(d => d.category === child && (d.status === "overdue" || d.status === "under_review"));
                    return (
                      <button key={child} onClick={() => setSelectedCategory(child)} className={`w-full text-left pl-10 pr-3 py-1 text-xs font-body rounded-lg ${selectedCategory === child ? "bg-[#288CC8]/10 text-[#288CC8] font-medium" : "text-muted-foreground hover:bg-muted"}`}>
                        {child} {count > 0 && <span className="text-muted-foreground">({count})</span>}
                        {hasWarning && <span className="ml-1 text-amber-400">⚠</span>}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>

        {/* Centre: Document List */}
        <div className="flex-1 min-w-0">
          {/* Filter bar */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title or reference..." className="pl-9 font-body text-sm" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px] font-body text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="superseded">Superseded</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => toast.success("Document register exported as CSV")} className="font-body text-xs">
              <Download className="w-3.5 h-3.5 mr-1" /> Export CSV
            </Button>
          </div>

          {/* Table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-body">
                <thead>
                  <tr className="bg-muted/50 text-muted-foreground text-[11px] uppercase tracking-wider">
                    <th className="text-left px-4 py-3">Ref</th>
                    <th className="text-left px-4 py-3">Title</th>
                    <th className="text-left px-4 py-3">Rev</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Ack Rate</th>
                    <th className="text-left px-4 py-3">Review Due</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocs.map(doc => {
                    const ackPct = Math.round((doc.ackRate[0] / doc.ackRate[1]) * 100);
                    return (
                      <tr key={doc.id} className="border-t border-border hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => setSelectedDoc(doc)}>
                        <td className="px-4 py-3 font-mono text-xs text-[#288CC8]">{doc.ref}</td>
                        <td className="px-4 py-3 text-foreground font-medium">{doc.title}</td>
                        <td className="px-4 py-3 text-muted-foreground">{doc.rev}</td>
                        <td className="px-4 py-3">{statusBadge(doc.status)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${ackPct < 70 ? "bg-red-400" : ackPct < 90 ? "bg-amber-400" : "bg-emerald-400"}`} style={{ width: `${ackPct}%` }} />
                            </div>
                            <span className="text-xs text-muted-foreground">{doc.ackRate[0]}/{doc.ackRate[1]}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-xs">{doc.reviewDue}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => setSelectedDoc(doc)}><Eye className="w-3.5 h-3.5" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setRevisionDoc(doc); setShowRevisionModal(true); }}><RotateCcw className="w-3.5 h-3.5" /></Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => toast.success(`${doc.ref} distributed to all assigned crew`)}><Send className="w-3.5 h-3.5" /></Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Right: Activity Feed */}
        <div className="w-full lg:w-[280px] shrink-0 space-y-4">
          {/* Pending Actions */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 font-body flex items-center gap-2">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> Pending Actions
            </h3>
            <div className="space-y-2">
              <div className="flex items-start gap-2 text-xs font-body">
                <span className="text-red-400 mt-0.5">●</span>
                <span className="text-foreground">OPS-003 acknowledgement overdue — 3 crew unacknowledged (21 days)</span>
              </div>
              <div className="flex items-start gap-2 text-xs font-body">
                <span className="text-amber-400 mt-0.5">●</span>
                <span className="text-foreground">SMS-001 scheduled review due in 14 days</span>
              </div>
              <div className="flex items-start gap-2 text-xs font-body">
                <span className="text-amber-400 mt-0.5">●</span>
                <span className="text-foreground">CRW-001 pending approval by COO</span>
              </div>
            </div>
          </div>

          {/* Activity Feed */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 font-body flex items-center gap-2">
              <Clock className="w-3.5 h-3.5" /> Activity Feed
            </h3>
            <div className="space-y-3">
              {ACTIVITY_FEED.map((a, i) => (
                <div key={i} className="text-xs font-body">
                  <p className="text-foreground">{a.text}</p>
                  <p className="text-muted-foreground mt-0.5">{a.time}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Monthly Stats */}
          <div className="bg-card border border-border rounded-xl p-4">
            <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3 font-body">Monthly Stats</h3>
            {[{ label: "Docs Reviewed", value: "4" }, { label: "New Revisions", value: "2" }, { label: "Ack Received", value: "23" }, { label: "Overdue Ack", value: "3" }].map(s => (
              <div key={s.label} className="flex justify-between text-xs font-body py-1 border-b border-border last:border-0">
                <span className="text-muted-foreground">{s.label}</span>
                <span className="text-foreground font-medium">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Document Detail Modal */}
      <Dialog open={!!selectedDoc} onOpenChange={() => setSelectedDoc(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          {selectedDoc && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-3">
                  <span className="text-[#288CC8] font-mono text-sm">{selectedDoc.ref}</span>
                  {selectedDoc.title}
                </DialogTitle>
                <div className="flex items-center gap-2 mt-2">
                  {statusBadge(selectedDoc.status)}
                  <span className="text-xs text-muted-foreground font-body">{selectedDoc.rev} · {selectedDoc.category}</span>
                </div>
              </DialogHeader>
              <Tabs defaultValue="document" className="mt-4">
                <TabsList className="font-body">
                  <TabsTrigger value="document">📄 Document</TabsTrigger>
                  <TabsTrigger value="distribution">📤 Distribution</TabsTrigger>
                  <TabsTrigger value="acknowledgement">✅ Acknowledgement</TabsTrigger>
                  <TabsTrigger value="audit">📋 Audit Log</TabsTrigger>
                </TabsList>
                <TabsContent value="document" className="space-y-4 mt-4">
                  <div className="bg-muted rounded-lg p-6 text-center border border-border">
                    <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground font-body">PDF document preview area</p>
                    <Button onClick={() => toast.success("Generating: Attach PDF", { description: "Your file will download shortly." })} size="sm" variant="outline" className="mt-3 font-body text-xs"><Upload className="w-3.5 h-3.5 mr-1" /> Attach PDF</Button>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm font-body">
                    <div><span className="text-muted-foreground">Author:</span> <span className="text-foreground font-medium ml-2">{selectedDoc.author}</span></div>
                    <div><span className="text-muted-foreground">Approver:</span> <span className="text-foreground font-medium ml-2">{selectedDoc.approver}</span></div>
                    <div><span className="text-muted-foreground">Issue Date:</span> <span className="text-foreground font-medium ml-2">{selectedDoc.issueDate}</span></div>
                    <div><span className="text-muted-foreground">Review Due:</span> <span className="text-foreground font-medium ml-2">{selectedDoc.reviewDue}</span></div>
                  </div>
                  {selectedDoc.revHistory.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 font-body">Revision History</h4>
                      <table className="w-full text-xs font-body">
                        <thead><tr className="text-muted-foreground"><th className="text-left py-1">Rev</th><th className="text-left py-1">Date</th><th className="text-left py-1">Change</th><th className="text-left py-1">Approved By</th></tr></thead>
                        <tbody>{selectedDoc.revHistory.map((r, i) => (<tr key={i} className="border-t border-border"><td className="py-1.5">{r.rev}</td><td>{r.date}</td><td>{r.change}</td><td>{r.approvedBy}</td></tr>))}</tbody>
                      </table>
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="distribution" className="space-y-4 mt-4">
                  <div className="text-sm font-body space-y-2">
                    <p className="text-muted-foreground">Assigned Roles: <span className="text-foreground">Master, Chief Officer, Engineer, All Crew, Shore Management</span></p>
                    <p className="text-muted-foreground">Vessels: <span className="text-foreground">{selectedDoc.vessels.join(", ")}</span></p>
                  </div>
                  <Button onClick={() => toast.success(`${selectedDoc.ref} distributed to all assigned crew`)} className="bg-[#288CC8] hover:bg-[#288CC8]/80 font-body text-sm">
                    <Send className="w-4 h-4 mr-2" /> Distribute Now
                  </Button>
                </TabsContent>
                <TabsContent value="acknowledgement" className="space-y-4 mt-4">
                  <div className="flex items-center gap-3 mb-4">
                    <Progress value={(selectedDoc.ackRate[0] / selectedDoc.ackRate[1]) * 100} className="flex-1 h-3" />
                    <span className="text-sm font-body font-medium">{selectedDoc.ackRate[0]}/{selectedDoc.ackRate[1]} crew acknowledged ({Math.round((selectedDoc.ackRate[0] / selectedDoc.ackRate[1]) * 100)}%)</span>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => toast.success("Reminders sent to unacknowledged crew")} className="font-body text-xs">
                      <Bell className="w-3.5 h-3.5 mr-1" /> Chase All Outstanding
                    </Button>
                  </div>
                </TabsContent>
                <TabsContent value="audit" className="space-y-2 mt-4">
                  {[`${selectedDoc.rev} issued — distributed to ${selectedDoc.ackRate[1]} crew — ${selectedDoc.issueDate}`, `Acknowledged by ${selectedDoc.ackRate[0]} crew members`, "System: Review date reminder scheduled"].map((log, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs font-body py-2 border-b border-border">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                      <span className="text-foreground">{log}</span>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Revision Modal */}
      <Dialog open={showRevisionModal} onOpenChange={setShowRevisionModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">New Revision — {revisionDoc?.ref}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-xs text-muted-foreground font-body mb-1 block">Change Description</label>
              <Textarea placeholder="Describe what changed and why..." className="font-body text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground font-body mb-1 block">New Revision</label>
                <Input value={revisionDoc ? `Rev ${parseInt(revisionDoc.rev.replace("Rev ", "")) + 1}` : ""} readOnly className="font-body text-sm" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground font-body mb-1 block">Effective Date</label>
                <Input type="date" className="font-body text-sm" />
              </div>
            </div>
            <Button onClick={() => { setShowRevisionModal(false); toast.success(`Revision submitted for ${revisionDoc?.ref} — pending approval`); }} className="w-full bg-[#288CC8] hover:bg-[#288CC8]/80 font-body">
              Submit for Approval
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const DocumentController = () => (
  <IntranetLayout>
    <DocumentControllerContent />
  </IntranetLayout>
);

export default DocumentController;
